/**
 * MyLearning Page
 * Personal learning dashboard with stats, charts, and progress tracking
 */

import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { StatsGridSkeleton, ChartSkeleton, HeatmapSkeleton } from '../components/ui/LoadingSkeleton';
import type { UserProgress, UserPathProgress, UserAchievement } from '../types/academy';
import { getUserAchievements, getTotalPoints } from '../services/achievementService';
import { calculateLevel } from '../data/achievements';

// Lazy load heavy components
const LearningStats = lazy(() => import('../components/academy/LearningStats').then(m => ({ default: m.LearningStats })));
const ProgressChart = lazy(() => import('../components/academy/ProgressChart').then(m => ({ default: m.ProgressChart })));
const ActivityHeatmap = lazy(() => import('../components/academy/ActivityHeatmap').then(m => ({ default: m.ActivityHeatmap })));

const STORAGE_KEYS = {
  PROGRESS: 'wealthhub_academy_progress',
  PATH_PROGRESS: 'wealthhub_academy_path_progress'
};

export function MyLearning() {
  const toast = useToast();
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState<Record<string, UserProgress>>({});
  const [pathProgress, setPathProgress] = useState<Record<string, UserPathProgress>>({});
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(1);

  // Load data from localStorage
  useEffect(() => {
    try {
      const savedProgress = localStorage.getItem(STORAGE_KEYS.PROGRESS);
      if (savedProgress) {
        setUserProgress(JSON.parse(savedProgress));
      }

      const savedPathProgress = localStorage.getItem(STORAGE_KEYS.PATH_PROGRESS);
      if (savedPathProgress) {
        setPathProgress(JSON.parse(savedPathProgress));
      }

      const achievements = getUserAchievements();
      setUserAchievements(achievements);

      const points = getTotalPoints(achievements);
      setTotalPoints(points);
      setCurrentLevel(calculateLevel(points));
    } catch (error) {
      console.error('Error loading learning data:', error);
    }
  }, []);

  // Calculate goals
  const goals = useMemo(() => {
    const completedCourses = Object.values(userProgress).filter(p => p.isCompleted).length;
    const totalWatchTime = Object.values(userProgress).reduce((sum, p) => sum + (p.totalDuration / 60), 0);

    return [
      {
        id: 'courses-10',
        title: 'Complete 10 Courses',
        current: completedCourses,
        target: 10,
        icon: '📚',
        color: '#10b981'
      },
      {
        id: 'watch-time',
        title: 'Watch 5 Hours',
        current: Math.round(totalWatchTime),
        target: 300,
        icon: '⏱️',
        color: '#3b82f6'
      },
      {
        id: 'achievements-10',
        title: 'Unlock 10 Achievements',
        current: userAchievements.length,
        target: 10,
        icon: '🏆',
        color: '#f59e0b'
      },
      {
        id: 'level-10',
        title: 'Reach Level 10',
        current: currentLevel,
        target: 10,
        icon: '⭐',
        color: '#a855f7'
      }
    ];
  }, [userProgress, userAchievements, currentLevel]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f0f0f',
      padding: '40px 20px',
      paddingBottom: '80px'
    }}>
      {/* Header */}
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: 700,
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icons.User size={36} />
            My Learning Dashboard
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--muted)' }}>
            Track your progress, analyze your learning patterns, and achieve your goals
          </p>
        </div>

        {/* Goals Section */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icons.Target size={20} />
            Your Goals
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {goals.map((goal) => {
              const progress = Math.min(100, (goal.current / goal.target) * 100);
              const isCompleted = progress >= 100;

              return (
                <div
                  key={goal.id}
                  style={{
                    padding: '20px',
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${goal.color}15 0%, ${goal.color}05 100%)`,
                    border: `1px solid ${goal.color}30`
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '28px' }}>{goal.icon}</span>
                    {isCompleted && (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        background: '#10b981',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>

                  <div style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '8px'
                  }}>
                    {goal.title}
                  </div>

                  <div style={{
                    fontSize: '13px',
                    color: 'var(--muted)',
                    marginBottom: '12px'
                  }}>
                    {goal.current} / {goal.target}
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    height: '8px',
                    borderRadius: '4px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${progress}%`,
                      background: goal.color,
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Statistics */}
        <div style={{ marginBottom: '40px' }}>
          <Suspense fallback={<StatsGridSkeleton />}>
            <LearningStats
              userProgress={userProgress}
              pathProgress={pathProgress}
              userAchievements={userAchievements}
              totalPoints={totalPoints}
              currentLevel={currentLevel}
            />
          </Suspense>
        </div>

        {/* Progress Chart */}
        <div style={{ marginBottom: '40px' }}>
          <Suspense fallback={<ChartSkeleton />}>
            <ProgressChart userProgress={userProgress} />
          </Suspense>
        </div>

        {/* Activity Heatmap */}
        <div style={{ marginBottom: '40px' }}>
          <Suspense fallback={<HeatmapSkeleton />}>
            <ActivityHeatmap userProgress={userProgress} />
          </Suspense>
        </div>

        {/* Quick Actions */}
        <div style={{
          padding: '32px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            marginBottom: '12px'
          }}>
            Ready to Continue Learning?
          </h2>
          <p style={{
            fontSize: '14px',
            color: 'var(--muted)',
            marginBottom: '24px'
          }}>
            Keep your streak going and unlock new achievements
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => navigate('/academy')}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                background: 'var(--accent)',
                border: 'none',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              Browse Courses
            </button>
            <button
              onClick={() => toast.info('Export feature coming soon!')}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              Export Progress Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
